import React from 'react';
import { IStorefrontSection } from '../../Api/Models/Products';
import { resolveStyleProps } from '../Helpers/stylesConfig';
import { useIsNarrow } from '../Helpers/useIsNarrow';

interface SectionRowLayoutProps {
    // Sections in render order (sortOrder). Order is preserved exactly.
    sections: IStorefrontSection[];
    // Per-section renderer. Whatever it returns is placed as-is for full-width sections, or inside a
    // flex item for fractional ones.
    renderSection: (section: IStorefrontSection, index: number) => React.ReactNode;
    // Gap between fractional sections packed into the same row (px).
    gap?: number;
    // Wraps a row (or standalone full section) in an edge-to-edge (100vw) background band. Supplied by
    // HomePage (reusing its fullBleedBand). Invoked only when a group/section opts into
    // fullWidthBackground; when absent, banding is skipped entirely.
    renderBand?: (backgroundColor: string, children: React.ReactNode, key: React.Key) => React.ReactNode;
}

const DEFAULT_GAP = 16;

// The row fraction each recognized width occupies. This table is the single source of truth for
// "is this section fractional?" — a width that is NOT a key here (absent, 'full', or a value this
// build doesn't know yet) renders full-width and standalone, which is the fail-safe.
export const SECTION_WIDTH_FRACTIONS: Record<string, number> = {
    quarter: 0.25,
    third: 1 / 3,
    half: 0.5,
    twoThirds: 2 / 3,
    threeQuarters: 0.75,
};

// True when the width packs into a shared row.
export const isFractionalWidth = (width?: string): boolean =>
    !!width && Object.prototype.hasOwnProperty.call(SECTION_WIDTH_FRACTIONS, width);

// True for the intrinsically narrow columns (fraction ≤ 1/3: 'quarter', 'third'). Renderers use this
// to switch to a stacked/vertical presentation that fits a skinny column.
export const isNarrowFractionalWidth = (width?: string): boolean =>
    isFractionalWidth(width) && SECTION_WIDTH_FRACTIONS[width as string] <= 1 / 3;

// Basis for a fractional column, discounting its share of the row gap so k columns + (k-1) gaps fit
// exactly one 100% row: a column of fraction f gets f of the width minus (1-f) of one gap. (For the
// pre-existing values this is byte-identical to the old hardcoded math — quarter → 25% - 12px,
// half → 50% - 8px at the default 16px gap.)
const basisFor = (width: IStorefrontSection['width'], gap: number): string => {
    const f = SECTION_WIDTH_FRACTIONS[width as string];
    // 4-dp rounding that drops trailing zeros (25, not 25.0000) — matches the admin preview's math.
    const round4 = (n: number) => Number(n.toFixed(4));
    return `calc(${round4(f * 100)}% - ${round4(gap * (1 - f))}px)`;
};

// Per-section vertical alignment within a shared row. Absent = the historical 'stretch' default.
const alignSelfFor = (align: IStorefrontSection['verticalAlign']): React.CSSProperties['alignSelf'] =>
    align === 'top' ? 'flex-start'
        : align === 'center' ? 'center'
        : align === 'bottom' ? 'flex-end'
        : undefined;

// Lays out the ordered section list. On desktop, consecutive fractional sections (any key of
// SECTION_WIDTH_FRACTIONS) pack left-to-right into a flex-wrap row (a full section, or an overflow,
// starts a new row). A full-width section (the default when width is absent) is emitted STANDALONE — not wrapped in any
// extra element — so when every section is full-width (today's data) the output is byte-identical to
// a plain `sections.map(renderSection)`. On narrow viewports everything stacks full-width in order.
const SectionRowLayout: React.FC<SectionRowLayoutProps> = ({ sections, renderSection, gap = DEFAULT_GAP, renderBand }) => {
    const isNarrow = useIsNarrow(700);

    // If any member opts into fullWidthBackground (and carries a backgroundColor), wrap `children` in a
    // full-bleed band colored by the FIRST such member. Otherwise pass the children through untouched
    // (a keyed Fragment adds no DOM element, so the unflagged / all-full-width output is unchanged).
    const withBand = (members: IStorefrontSection[], children: React.ReactNode, key: React.Key): React.ReactNode => {
        // Read the band color through named-style resolution so a section whose styleProps only
        // carry a `$styleRef` (background supplied by the referenced section style's `band` part)
        // still paints its full-width band. Without a ref this is the section's own dictionary.
        const colorOf = (s: IStorefrontSection) => resolveStyleProps(s.styleProps, 'band')?.backgroundColor as string | undefined;
        const flagged = members.find(s => s.fullWidthBackground && colorOf(s));
        const color = flagged ? colorOf(flagged) : undefined;
        return color && renderBand
            ? renderBand(color, children, key)
            : <React.Fragment key={key}>{children}</React.Fragment>;
    };

    const out: React.ReactNode[] = [];
    let group: { section: IStorefrontSection; index: number }[] = [];

    const flushGroup = () => {
        if (group.length === 0) return;
        const rowKey = `row-${group[0].index}`;
        const members = group.map(g => g.section);
        // Mobile: no flex packing — the group's members stack full-width in order. Desktop: pack into a
        // flex-wrap row. Either way, a single band (if flagged) wraps the whole group.
        const inner = isNarrow
            ? <>{group.map(({ section, index }) => (
                  <React.Fragment key={index}>{renderSection(section, index)}</React.Fragment>
              ))}</>
            : <div style={{ display: 'flex', flexWrap: 'wrap', gap, alignItems: 'stretch' }}>
                  {group.map(({ section, index }) => (
                      // containerType: inline-size makes each column a query container, so CSS can
                      // adapt the content to the COLUMN's width rather than the viewport's (a
                      // third/quarter column is ~256/188px wide inside the default 800px content
                      // column, far below any viewport breakpoint). See the @container rules in
                      // index.css for the product cards and banner overlays.
                      <div key={index} style={{ flex: `0 0 ${basisFor(section.width, gap)}`, boxSizing: 'border-box', minWidth: 0, containerType: 'inline-size', alignSelf: alignSelfFor(section.verticalAlign) }}>
                          {renderSection(section, index)}
                      </div>
                  ))}
              </div>;
        out.push(withBand(members, inner, rowKey));
        group = [];
    };

    sections.forEach((section, index) => {
        // Fractional sections group (desktop packs them into a row; mobile stacks them but keeps the
        // group so one band still covers the set). Full-width sections flush the group and stand alone.
        if (isFractionalWidth(section.width)) {
            group.push({ section, index });
        } else {
            flushGroup();
            out.push(withBand([section], renderSection(section, index), `full-${index}`));
        }
    });
    flushGroup();

    return <>{out}</>;
};

export default SectionRowLayout;
